<template>
  <div>
    <div class="kb-list-head">
      <span class="kb-row-meta">
        <template v-if="contactsArray.length === 0">No emergency contacts added yet</template>
        <template v-else>
          {{ contactsArray.length }} {{ contactsArray.length === 1 ? 'contact' : 'contacts' }}
        </template>
      </span>
      <Button
        v-if="!disabled"
        label="Add contact"
        icon="pi pi-plus"
        text
        size="small"
        @click="addNewContact"
      />
    </div>

    <ul
      v-if="contactsArray.length > 0"
      class="kb-rows m-0 p-0 list-none"
    >
      <li
        v-for="(contact, index) in contactsArray"
        :key="`${contact.name}-${contact.phone}`"
        class="kb-row"
        :class="{ 'kb-row-link': !disabled }"
      >
        <!-- The whole row opens the edit dialog (which also holds Remove) -->
        <button
          type="button"
          class="kb-row-open"
          :disabled="disabled"
          @click="editContact(index)"
        >
          <span class="kb-row-title block">
            {{ contact.name }}<span
              v-if="index === 0"
              class="kb-tag kb-tag-neutral"
            >Primary</span>
          </span>
          <span class="kb-row-meta block mt-0.5">
            {{ formatPhone(contact.phone) }}<template v-if="contact.relationship">
              · {{ contact.relationship }}
            </template>
          </span>
        </button>
        <div class="kb-row-aside">
          <a
            v-tooltip.top="'Call'"
            :href="'tel:' + contact.phone.replace(/[^+\d]/g, '')"
            class="kb-contact-call"
            :aria-label="`Call ${contact.name}`"
          ><i class="pi pi-phone" /></a>
          <i
            v-if="!disabled"
            class="pi pi-angle-right kb-row-chevron"
            aria-hidden="true"
          />
        </div>
      </li>
    </ul>

    <!-- Add / edit an emergency contact -->
    <Dialog
      v-model:visible="showDialog"
      :header="editingIndex !== null ? 'Edit emergency contact' : 'Add emergency contact'"
      modal
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      class="emergency-contact-dialog"
    >
      <div class="kb-form">
        <div class="kb-field">
          <label for="contactName">Name</label>
          <InputText
            id="contactName"
            v-model="contactForm.name"
            size="small"
            fluid
            autofocus
            :invalid="submitted && !contactForm.name"
            aria-describedby="name-error"
            :disabled="disabled"
          />
          <small
            v-if="submitted && !contactForm.name"
            id="name-error"
            class="kb-field-error"
          >
            Name is required
          </small>
        </div>

        <div class="kb-field">
          <label for="contactPhone">Phone</label>
          <KbPhoneInput
            id="contactPhone"
            v-model="contactForm.phone"
            :invalid="submitted && !isValidPhone"
            :aria-describedby="submitted && !isValidPhone ? 'phone-error' : undefined"
            :disabled="disabled"
          />
          <small
            v-if="submitted && !isValidPhone"
            id="phone-error"
            class="kb-field-error"
          >
            Enter the full phone number
          </small>
        </div>

        <div class="kb-field">
          <label for="contactRelationship">Relationship <span class="kb-field-optional">(optional)</span></label>
          <InputText
            id="contactRelationship"
            v-model="contactForm.relationship"
            size="small"
            fluid
            placeholder="Spouse, parent, friend…"
            :disabled="disabled"
          />
        </div>
      </div>

      <template #footer>
        <Button
          v-if="editingIndex !== null && !disabled && (contactsArray.length > 1 || !requireAtLeastOne)"
          label="Remove"
          icon="pi pi-trash"
          text
          severity="danger"
          class="mr-auto"
          @click="removeEditingContact"
        />
        <Button
          label="Cancel"
          text
          severity="secondary"
          @click="closeDialog"
        />
        <Button
          :label="editingIndex !== null ? 'Save' : 'Add contact'"
          :disabled="disabled"
          @click="saveContactForm"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, reactive, ref } from 'vue'
import { formatPhone, isValidPhone as isCompletePhone, normalizePhone } from '~/utils/phone'

interface EmergencyContact {
  name: string
  phone: string
  relationship?: string
}

const props = defineProps({
  contacts: {
    type: Array as () => EmergencyContact[],
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  requireAtLeastOne: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update'])

const confirm = useConfirm()
const toast = useToast()

const showDialog = ref(false)
const editingIndex = ref<number | null>(null)
const submitted = ref(false)

const contactForm = reactive({
  name: '',
  phone: '',
  relationship: '',
})

// Convert to array for display
const contactsArray = computed(() => props.contacts || [])

// A complete number, as the app's other phone fields check it. (This list
// used to accept 10 bare digits and store them as "+" + digits, which drops
// the US country code: "+5045551234" dials Honduras.)
const isValidPhone = computed(() => isCompletePhone(contactForm.phone))

const resetForm = () => {
  const contact = editingIndex.value !== null
    ? contactsArray.value[editingIndex.value]
    : undefined
  if (contact) {
    contactForm.name = contact.name || ''
    contactForm.phone = contact.phone || ''
    contactForm.relationship = contact.relationship || ''
  }
  else {
    contactForm.name = ''
    contactForm.phone = ''
    contactForm.relationship = ''
  }
  submitted.value = false
}

const addNewContact = () => {
  if (props.disabled) return
  editingIndex.value = null
  resetForm()
  showDialog.value = true
}

const editContact = (index: number) => {
  if (props.disabled) return
  editingIndex.value = index
  resetForm()
  showDialog.value = true
}

const closeDialog = () => {
  showDialog.value = false
}

const validateForm = () => {
  submitted.value = true
  return contactForm.name.trim() && isValidPhone.value
}

const saveContactForm = () => {
  if (props.disabled) return
  if (!validateForm()) return

  const newContact: EmergencyContact = {
    name: contactForm.name.trim(),
    phone: normalizePhone(contactForm.phone),
    relationship: contactForm.relationship.trim() || undefined,
  }

  const updatedContacts = [...contactsArray.value]

  if (editingIndex.value !== null) {
    // Update existing
    updatedContacts[editingIndex.value] = newContact
    toast.add({
      severity: 'success',
      summary: 'Contact Updated',
      detail: `${newContact.name} has been updated`,
      life: 3000,
    })
  }
  else {
    // Add new
    updatedContacts.push(newContact)
    toast.add({
      severity: 'success',
      summary: 'Contact Added',
      detail: `${newContact.name} has been added`,
      life: 3000,
    })
  }

  emit('update', updatedContacts)
  closeDialog()
}

// Remove lives in the edit dialog now: close it, then confirm as before
const removeEditingContact = () => {
  const index = editingIndex.value
  if (index === null) return
  showDialog.value = false
  confirmRemoveContact(index)
}

const confirmRemoveContact = (index: number) => {
  if (props.disabled) return

  const contact = contactsArray.value[index]
  if (!contact) return

  confirm.require({
    message: `Are you sure you want to remove ${contact.name}?`,
    header: 'Confirm Removal',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => {
      const updatedContacts = contactsArray.value.filter((_, i) => i !== index)
      emit('update', updatedContacts)
      toast.add({
        severity: 'success',
        summary: 'Contact Removed',
        detail: `${contact.name} has been removed`,
        life: 3000,
      })
    },
  })
}
</script>

<style scoped>
:deep(.emergency-contact-dialog .p-dialog-content) {
  overflow: visible !important;
}
/* Call link: the one thing you do with a contact without editing it */
.kb-contact-call {
  display: inline-grid;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  color: var(--kb-muted);
}

.kb-contact-call:hover {
  color: var(--kb-accent);
  background: var(--kb-hover);
}

.kb-contact-call .pi {
  font-size: 0.75rem;
}
</style>
